
import { Component, OnInit, Inject, OnDestroy, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest, EventMessage, EventType, InteractionType, AccountInfo } from '@azure/msal-browser';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ODataService } from './odata.service';

const DEFAULT_ROUTE = '/players/dashboard';
const ACCOUNT_PENDING_ROUTE = '/account-pending';
const POST_LOGOUT_STORAGE_CLEAR_KEY = 'postLogoutStorageClear';
const LOGOUT_REDIRECT_DELAY_MS = 100;

export type UserRegistrationStatus = 'unknown' | 'registered' | 'not_registered';


@Injectable({
    providedIn: 'root'
})
export class AuthService implements OnDestroy {
    isIframe = false;
    loginDisplay = false;
    idTokenClaims: any;
    userData: any = {};
    get email(): string | null {
        return this.idTokenClaims ? this.idTokenClaims.user_email : null;
    }
    get userNotRegistered(): boolean {
        return this.userRegistrationStatus$.value === 'not_registered';
    }
    private readonly _destroying$ = new Subject<void>();
    private readonly userDataUpdated$ = new Subject<void>();
    readonly userDataUpdated = this.userDataUpdated$.asObservable();
    private readonly userRegistrationStatus$ = new BehaviorSubject<UserRegistrationStatus>('unknown');
    readonly userRegistrationStatus = this.userRegistrationStatus$.asObservable();
    private userDataFetchEmail: string | null = null;

    constructor(
        @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private ODataService: ODataService,
        private router: Router
    ) { }

    getUserRoleIds(): number[] {
        return (this.userData?.roles ?? [])
            .map((userRole: { roleId?: number }) => userRole.roleId)
            .filter((roleId: number | undefined): roleId is number => roleId != null);
    }

    hasRole(role: number): boolean {
        return this.getUserRoleIds().includes(role);
    }

    setUserData() {
        const email = this.idTokenClaims?.user_email;
        if (!email) {
            return;
        }

        const cachedUserData = localStorage.getItem("userData");
        if (cachedUserData) {
            const parsed = JSON.parse(cachedUserData);
            if (parsed?.email === email && parsed?.id && Array.isArray(parsed?.roles)) {
                this.applyRegisteredUser(parsed);
                return;
            }
        }

        if (this.userDataFetchEmail === email) {
            return;
        }

        this.fetchUserData(email);
    }

    refreshUserData(): void {
        const email = this.idTokenClaims?.user_email;
        if (!email) {
            return;
        }

        localStorage.removeItem("userData");
        this.userDataFetchEmail = null;
        this.userRegistrationStatus$.next('unknown');
        this.fetchUserData(email);
    }

    private fetchUserData(email: string): void {
        localStorage.removeItem("userData");
        this.userDataFetchEmail = email;
        this.ODataService.getEntityDataById('user', { email }, { roles: {} }).subscribe({
            next: (user) => {
                if (!user) {
                    this.applyUnregisteredUser();
                    return;
                }

                this.applyRegisteredUser(user);
            },
            error: () => {
                this.userDataFetchEmail = null;
                this.userRegistrationStatus$.next('unknown');
                this.applyUnregisteredUser();
            }
        });
    }

    private applyRegisteredUser(user: any): void {
        this.userData = user;
        this.userDataFetchEmail = user?.email ?? this.idTokenClaims?.user_email ?? null;
        this.syncTokenClaimsToUserData();
        localStorage.setItem("userData", JSON.stringify(this.userData));
        this.userRegistrationStatus$.next('registered');
        this.notifyUserDataUpdated();
        this.navigateToDefaultIfOnLanding();
    }

    private applyUnregisteredUser(): void {
        this.userData = {};
        this.userRegistrationStatus$.next('not_registered');
        this.notifyUserDataUpdated();
        this.router.navigateByUrl(ACCOUNT_PENDING_ROUTE, { replaceUrl: true });
    }

    private syncTokenClaimsToUserData(): void {
        if (this.userData && this.idTokenClaims) {
            this.userData.permissions = this.idTokenClaims.permissions;
        }
    }

    private notifyUserDataUpdated(): void {
        this.userDataUpdated$.next();
    }
    init(): void {
        this.clearStorageIfPostLogout();
        this.isIframe = window !== window.parent && !window.opener;
        this.setLoginDisplay();

        this.authService.instance.enableAccountStorageEvents();
        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
            )
            .subscribe((result: EventMessage) => {
                if (this.authService.instance.getAllAccounts().length === 0) {
                    window.location.pathname = "/";
                } else {
                    this.setLoginDisplay();
                }
            });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                this.setLoginDisplay();
                this.checkAndSetActiveAccount();
            })

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
                takeUntil(this._destroying$)
            )
            .subscribe((result: EventMessage) => {
                const payload = result.payload as AuthenticationResult;
                this.authService.instance.setActiveAccount(payload.account);
                this.setLoginDisplay();
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                this.completePostLogoutStorageClear();
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
                takeUntil(this._destroying$)
            )
            .subscribe((result: EventMessage) => {
                console.error('Authentication Error:', result.error);
            });
    }

    setLoginDisplay() {
        let accounts = this.authService.instance.getAllAccounts();
        if (accounts.length > 0) {
            this.loginDisplay = true;
            const account = this.authService.instance.getActiveAccount() ?? accounts[0];
            this.idTokenClaims = account.idTokenClaims;
            this.setUserData();
        } else {
            this.loginDisplay = false;
            this.idTokenClaims = null;
            this.userData = {};
            this.userDataFetchEmail = null;
            this.userRegistrationStatus$.next('unknown');
            this.notifyUserDataUpdated();
        }
    }

    checkAndSetActiveAccount() {
        let activeAccount = this.authService.instance.getActiveAccount();

        if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
            let accounts = this.authService.instance.getAllAccounts();
            this.authService.instance.setActiveAccount(accounts[0]);
        }
    }

    loginRedirect() {
        this.runWhenInteractionComplete(() => this.startLoginRedirect());
    }

    login() {
        this.runWhenInteractionComplete(() => {
            if (this.authService.instance.getAllAccounts().length > 0) {
                this.setLoginDisplay();
                return;
            }

            this.clearAppCache();
            if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
                if (this.msalGuardConfig.authRequest) {
                    this.authService.loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
                        .subscribe((response: AuthenticationResult) => {
                            this.authService.instance.setActiveAccount(response.account);
                        });
                } else {
                    this.authService.loginPopup()
                        .subscribe((response: AuthenticationResult) => {
                            this.authService.instance.setActiveAccount(response.account);
                        });
                }
            } else {
                this.startLoginRedirect();
            }
        });
    }

    private runWhenInteractionComplete(action: () => void): void {
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                take(1),
                takeUntil(this._destroying$)
            )
            .subscribe(action);
    }

    private startLoginRedirect(): void {
        if (this.msalGuardConfig.authRequest) {
            this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
        } else {
            this.authService.loginRedirect();
        }
    }

    logout() {
        this.runWhenInteractionComplete(() => {
            void this.performLogout();
        });
    }

    private async performLogout(): Promise<void> {
        const accounts = this.authService.instance.getAllAccounts();
        const primaryAccount = this.authService.instance.getActiveAccount() ?? accounts[0];

        this.loginDisplay = false;
        this.userData = {};
        this.idTokenClaims = null;
        this.userDataFetchEmail = null;
        this.userRegistrationStatus$.next('unknown');

        await Promise.all(
            accounts.map(account => this.authService.instance.logout({ account }))
        );
        this.authService.instance.setActiveAccount(null);

        this.markPostLogoutStorageClear();
        this.clearAllStorage();

        if (!primaryAccount) {
            this.completePostLogoutStorageClear();
            return;
        }

        const logoutRequest = {
            account: primaryAccount,
            postLogoutRedirectUri: environment.postLogoutRedirectUri || '/',
            logoutHint: primaryAccount.username
        };

        setTimeout(() => {
            if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
                this.authService.logoutPopup({
                    ...logoutRequest,
                    mainWindowRedirectUri: '/'
                }).subscribe(() => this.completePostLogoutStorageClear());
            } else {
                this.authService.logoutRedirect(logoutRequest);
            }
        }, LOGOUT_REDIRECT_DELAY_MS);
    }

    private markPostLogoutStorageClear(): void {
        sessionStorage.setItem(POST_LOGOUT_STORAGE_CLEAR_KEY, 'true');
    }

    private clearStorageIfPostLogout(): void {
        if (sessionStorage.getItem(POST_LOGOUT_STORAGE_CLEAR_KEY) !== 'true') {
            return;
        }

        this.completePostLogoutStorageClear();
    }

    private completePostLogoutStorageClear(): void {
        sessionStorage.removeItem(POST_LOGOUT_STORAGE_CLEAR_KEY);
        this.clearAllStorage();
        this.loginDisplay = false;
        this.idTokenClaims = null;
        this.userDataFetchEmail = null;
        this.userRegistrationStatus$.next('unknown');
    }

    private clearAllStorage(): void {
        localStorage.clear();
        this.userData = {};
        this.notifyUserDataUpdated();
    }

    private navigateToDefaultIfOnLanding(): void {
        const url = this.router.url.split('?')[0].split('#')[0];
        if (url === '/' || url === '' || url === ACCOUNT_PENDING_ROUTE) {
            this.router.navigateByUrl(DEFAULT_ROUTE, { replaceUrl: true });
        }
    }

    private clearAppCache(): void {
        localStorage.removeItem('userData');
        this.userData = {};
        this.userDataFetchEmail = null;
        this.userRegistrationStatus$.next('unknown');
        this.notifyUserDataUpdated();
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
        this.userDataUpdated$.complete();
        this.userRegistrationStatus$.complete();
    }
}
